import {
  Flex,
} from "@chakra-ui/react";
import { Alert, Input } from "@/components";
import { WithAuth } from "@/hoc";
import { useClient } from "@/hooks";
import { ClientData, ClientsAddressData } from "@/models/Clients";
import { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  phone_format,
  clearMask,
  showToast,
} from "@/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import ChargeHistory from "./_charges-history";
import { EditConfirmationModal } from "./_editConfirmationModal";
import { Button, ContentRow, PageHeading, Section } from "barte-design-system";
import { PhoneInput2 } from "@/components/Forms/PhoneInput2";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const ClientDetails = () => {
  const infoValidationSchema = Yup.object().shape({
    name: Yup.string().required("Preenchimento obrigatório!"),
    countryCode: Yup.string().required(),
    phone: Yup.string().required("Preenchimento obrigatório!"),
    email: Yup.string()
      .email("Digite um e-mail válido!")
      .required("O campo e-mail é obrigatório!"),
  });
  
  const addressValidationSchema = Yup.object().shape({
    zipCode: Yup.string().required("Preenchimento obrigatório!"),
    street: Yup.string().required("Preenchimento obrigatório!"),
    district: Yup.string().required("Preenchimento obrigatório!"),
    city: Yup.string().required("Preenchimento obrigatório!"),
    state: Yup.string().required("Preenchimento obrigatório!"),
    country: Yup.string().required("Preenchimento obrigatório!"),
  });

  const [editButtonState, setEditButtonState] = useState(false);
  const [cancelAlertState, setCancelAlertState] = useState(false);
  const [editAlertState, setEditAlertState] = useState(false);
  const [editAddressButtonState, setEditAddressButtonState] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [data, setData] = useState<ClientData>({} as ClientData);
  const [loading, setLoading] = useState(false);
  const { putClient, getClient, patchClientAddress, consultCep } = useClient();
  const [formattedDocument, setFormattedDocument] = useState("");
  const [validationSchema, setValidationSchema] = useState({} as any);
  const cancelRef = useRef();
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const cep = watch("zipCode");
  const countryCodeValue = watch("countryCode");
  const phoneValue = watch("phone");
  const editedValues = watch(["name", "phone", "email", "alternativeEmail"]);

  const fullPhoneE164 = countryCodeValue && phoneValue
    ? `${countryCodeValue}${clearMask(phoneValue)}`
    : undefined;

  function handlePhoneChange(value?: string) {
    if (!value) {
      setValue("phone", "");
      setValue("countryCode", "");
      return;
    }
    const parsed = parsePhoneNumberFromString(value);
    if (parsed) {
      setValue("countryCode", `+${parsed.countryCallingCode}`);
      setValue("phone", phone_format(parsed.nationalNumber));
    }
  }

  useEffect(() => {
    if (id === undefined) return;
    GetClientData();
  }, [id]);

  async function GetClientData() {
    try {
      const response = await getClient(Number(id));
      setFormattedDocument(response.data.document);
      setData({
        ...response.data,
        phone: phone_format(response.data.phone),
      });
      Object.keys(response.data).map(value => {
        if(value === "document") return
        if(typeof response.data[value] === "object") {
          Object.keys(response.data[value]).map(addressValue => {
            setValue(addressValue, response.data[value][addressValue]);
          })
        }
        if(value === "phone") return setValue(value, phone_format(response.data[value]));
        setValue(value, response.data[value]);
      });
    } catch (error) {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
    }
  }

  async function handleCancelEdit() {
    setCancelAlertState(false);
    setEditAddressButtonState(false);
    setEditButtonState(false);
    GetClientData();
    setValidationSchema({} as any);
  }
  async function handleGetCep() {
    try {
        clearErrors("zipCode");
        setIsCepLoading(true);
        const { data } = await consultCep(clearMask(cep));
        setValue("street", data.logradouro);
        setValue("district", data.bairro);
        setValue("city", data.localidade);
        setValue("state", data.uf);
        setValue("complement", data.complemento);
        setValue("country", "BR");
    } catch {
        if(cep.length !== 10) return setError("zipCode", { type: "custom", message:"Cep inválido."})
    } finally {
        setIsCepLoading(false);
    }
}

  const handleUpdateClient: SubmitHandler<ClientData> = async (values) => {
    setLoading(true);
    const client = {
      document: clearMask(data.document),
      name: values.name,
      email: values.email,
      phone: clearMask(values.phone),
      countryCode: values.countryCode,
      alternativeEmail: values.alternativeEmail,
    };
    try {
      await putClient(data.id, client);
      showToast({
        message: "Cliente editado com sucesso!",
        type: "success",
      });
    } catch (error) {
      error.response.data.errors.forEach((item) => {
        showToast({
          message: item.description,
          type: "error",
        });
      });
    } finally {
      setLoading(false);
      setEditButtonState(!editButtonState);
      setEditAlertState(false)
      GetClientData();
    }
    
  };

  const handleUpdateClientAddress: SubmitHandler<ClientsAddressData> = async (values) => {
    setLoading(true);
    if(Object.keys(errors)?.length > 0) return;
    const clientAddress = {
      zipCode: clearMask(values.zipCode),
      street: values.street,
      district: values.district,
      number: Number(values.number),
      complement: values.complement,
      city: values.city,
      state: values?.state?.toLocaleUpperCase(),
      country: values.country,
    }
    try {
      await patchClientAddress(data.id, clientAddress);
      showToast({
        message: "Endereço editado com sucesso!",
        type: "success",
      });
    } catch (error) {
      error.response.data.errors.forEach((item) => {
        showToast({
          message: item.description,
          type: "error",
        });
      });
    } finally {
      setEditAddressButtonState(!editAddressButtonState);
      setLoading(false);
      GetClientData();
    }
  }

  return (
    <>
      <Alert
        closeButtonText="Não"
        confirmButtonText="Cancelar"
        isConfirmAlert={false}
        isOpen={cancelAlertState}
        onConfirm={handleCancelEdit}
        onClose={() => setCancelAlertState(!cancelAlertState)}
        title="Tem certeza que deseja cancelar?"
        messageBody="Todas as alterações serão perdidas"
        leastDestructiveRef={cancelRef}
      />
      <EditConfirmationModal
        isOpen={editAlertState}
        onClose={() => setEditAlertState(false)}
        onConfirm={handleSubmit(handleUpdateClient)}
        clientData={data}
        editedData={editedValues}
      />
      <Flex
        w="100%"
        flexDir="column"

      >
   
        <PageHeading
          onBack={() => navigate(-1)}
          title={data.name || "Clientes"}
          style={{marginBottom: "1rem"}}
        />

        <Flex
          as="form"
          onSubmit={handleSubmit(handleUpdateClient)}
          flexDirection="column"
        >
          <Section
            title="Identificação e contato"
            variant="two-columns"
            headerActions={
              <>
                {!editButtonState && (
                    <Button
                      type="button"
                      onClick={() => {
                        setEditButtonState(!editButtonState);
                        setValidationSchema(infoValidationSchema);
                      }}
                      disabled={editAddressButtonState}
                    >
                      Editar
                    </Button>
                )}

                {editButtonState && (
                  <Flex gap="2">
                    <Button
                      type="button"
                      onClick={() => { setCancelAlertState(!cancelAlertState) }}
                      variant="outline"
                      loading={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      loading={loading}
                      onClick={() => setEditAlertState(!editAlertState)}
                    >
                      Concluir
                    </Button>
                  </Flex>
                )}
              </>
            }
            secondColumn={
              <>
                <ContentRow
                  label="Documento"
                  content={formattedDocument || "-"}
                />
                <ContentRow
                  label="Email"
                  content={
                    editButtonState ? (
                      <Input
                        compact
                        type="text"
                        name="email"
                        register={{ ...register("email") }}
                        errors={errors?.email}
                        containerDisabled={editButtonState}
                      />
                    ) : (
                      data.email || "-"
                    )
                  }
                />
              </>
            }
          >
            <ContentRow
              label="Nome"
              content={
                editButtonState ? (
                  <Input
                    compact
                    type="text"
                    name="name"
                    register={{ ...register("name") }}
                    errors={errors?.name}
                    containerDisabled={editButtonState}
                  />
                ) : (
                  data.name || "-"
                )
              }
            />
            <ContentRow
              label="Telefone"
              content={
                editButtonState ? (
                  <PhoneInput2
                    type="text"
                    name="phone"
                    valueData={fullPhoneE164}
                    setValue={handlePhoneChange}
                    errors={errors?.phone}
                    isDisabled={!editButtonState}
                  />
                ) : (
                  `${data.countryCode || ""} ${data.phone || ""}`.trim() || "-"
                )
              }
            />
            <ContentRow
              label="Email financeiro"
              content={
                editButtonState ? (
                  <Input
                    compact
                    type="text"
                    name="alternativeEmail"
                    register={{ ...register("alternativeEmail") }}
                    errors={errors?.alternativeEmail}
                    containerDisabled={editButtonState}
                  />
                ) : (
                  data.alternativeEmail || "-"
                )
              }
            />
          </Section>
        </Flex>

        <Flex
          as="form"
          onSubmit={handleSubmit(handleUpdateClientAddress)}
          flexDirection="column"
          mt="2rem"
        >
          <Section
            title="Endereço"
            variant="two-columns"
            headerActions={
              <>
                {!editAddressButtonState && (
                      <Button
                        type="button"
                        onClick={() => {
                          setEditAddressButtonState(!editAddressButtonState);
                          setValidationSchema(addressValidationSchema);
                        }}
                        disabled={editButtonState}
                      >
                        Editar
                      </Button>
                )}

                {editAddressButtonState && (
                  <Flex gap="2">
                    <Button
                      type="button"
                      onClick={() => { setCancelAlertState(!cancelAlertState) }}
                      variant="outline"
                      loading={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      loading={loading}
                      onClick={() => setEditAlertState(!editAlertState)}
                    >
                      Concluir
                    </Button>
                  </Flex>
                )}
              </>
            }
            secondColumn={
              <>
                <ContentRow
                  label="Rua/Avenida"
                  content={
                    editAddressButtonState ? (
                      <Input
                        compact
                        type="text"
                        name="street"
                        register={{ ...register("street") }}
                        errors={errors?.street}
                        containerDisabled={editAddressButtonState && !isCepLoading}
                      />
                    ) : (
                      data.address?.street || "-"
                    )
                  }
                />
                <ContentRow
                  label="Complemento"
                  content={
                    editAddressButtonState ? (
                      <Input
                        compact
                        type="text"
                        name="complement"
                        register={{ ...register("complement") }}
                        errors={errors?.complement}
                        containerDisabled={editAddressButtonState && !isCepLoading}
                      />
                    ) : (
                      data.address?.complement || "-"
                    )
                  }
                />
                <ContentRow
                  label="Cidade"
                  content={
                    editAddressButtonState ? (
                      <Input
                        compact
                        type="text"
                        name="city"
                        register={{ ...register("city") }}
                        mask="onlyLetters"
                        errors={errors?.city}
                        containerDisabled={editAddressButtonState && !isCepLoading}
                      />
                    ) : (
                      data.address?.city || "-"
                    )
                  }
                />
                <ContentRow
                  label="País"
                  content={
                    editAddressButtonState ? (
                      <Input
                        compact
                        type="text"
                        name="country"
                        register={{ ...register("country") }}
                        maxLength={2}
                        mask="onlyLetters"
                        errors={errors?.country}
                        containerDisabled={editAddressButtonState && !isCepLoading}
                      />
                    ) : (
                      data.address?.country || "-"
                    )
                  }
                />
              </>
            }
          >
            <ContentRow
              label="CEP"
              content={
                editAddressButtonState ? (
                  <Input
                    compact
                    type="text"
                    name="zipCode"
                    register={{ ...register("zipCode") }}
                    errors={errors?.zipCode}
                    containerDisabled={editAddressButtonState && !isCepLoading}
                    onBlur={handleGetCep}
                    mask="cep"
                  />
                ) : (
                  data.address?.zipCode || "-"
                )
              }
            />
            <ContentRow
              label="Número"
              content={
                editAddressButtonState ? (
                  <Input
                    compact
                    type="text"
                    name="number"
                    register={{ ...register("number") }}
                    errors={errors?.number}
                    containerDisabled={editAddressButtonState && !isCepLoading}
                  />
                ) : (
                  data.address?.number || "-"
                )
              }
            />
            <ContentRow
              label="Bairro"
              content={
                editAddressButtonState ? (
                  <Input
                    compact
                    type="text"
                    name="district"
                    register={{ ...register("district") }}
                    errors={errors?.district}
                    containerDisabled={editAddressButtonState && !isCepLoading}
                  />
                ) : (
                  data.address?.district || "-"
                )
              }
            />
            <ContentRow
              label="Estado"
              content={
                editAddressButtonState ? (
                  <Input
                    compact
                    type="text"
                    name="state"
                    register={{ ...register("state") }}
                    maxLength={2}
                    mask="onlyLetters"
                    errors={errors?.state}
                    containerDisabled={editAddressButtonState && !isCepLoading}
                  />
                ) : (
                  data.address?.state || "-"
                )
              }
            />
          </Section>
        </Flex>

        <ChargeHistory client={data} />
      </Flex>
    </>
  );
};

export default WithAuth(ClientDetails);
